import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { trackEvent } from "@/lib/audit";

/**
 * P0-NATION-01 — Compact "Tu nación está #N del mundo" badge.
 * Reuses the existing nation_scores ranking already fetched elsewhere;
 * keeps query lightweight and caches result per nation.
 */
interface Row { nation_code: string; nation_name: string; flag?: string | null }

export function NationPositionBadge() {
  const { nation } = useSelectedNation();
  const [rank, setRank] = useState<number | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [flag, setFlag] = useState<string>("🏳️");
  const [name, setName] = useState<string>("");

  useEffect(() => {
    if (!nation) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("nation_scores")
        .select("nation_code,nation_name")
        .order("total_points", { ascending: false });
      const rows = (data ?? []) as Row[];
      if (!alive) return;
      const idx = rows.findIndex((r) => r.nation_code === nation);
      setRank(idx >= 0 ? idx + 1 : null);
      setTotal(rows.length);
      const me = rows.find((r) => r.nation_code === nation);
      if (me) setName(me.nation_name);
      // best-effort flag from worldcup_teams
      const { data: t } = await supabase
        .from("worldcup_teams")
        .select("flag,name")
        .eq("code", nation)
        .maybeSingle();
      if (!alive) return;
      if (t?.flag) setFlag(t.flag);
      if (t?.name) setName(t.name);
    })();
    return () => { alive = false; };
  }, [nation]);

  if (!nation || rank === null) return null;

  return (
    <Link
      to="/nation-wars"
      onClick={() => void trackEvent("nation_rank_badge_clicked", { nation, rank, total })}
      className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-[11px] font-black text-foreground transition hover:bg-primary/20"
    >
      <span className="text-base leading-none">{flag}</span>
      <span>
        Tu nación está <span className="text-primary">#{rank}</span> del mundo
        {name ? <span className="ml-1 text-muted-foreground">· {name}</span> : null}
      </span>
    </Link>
  );
}
